import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';

const TABS = ['Events', 'Festivals'];

function PromoterDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [photosOpen, setPhotosOpen] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [photosLoading, setPhotosLoading] = useState(false);
    const [photosError, setPhotosError] = useState('');
    const [photosLoaded, setPhotosLoaded] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [search, setSearch] = useState('');
    const [expandedFestival, setExpandedFestival] = useState(null);

    // Attended / performed state
    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (eventList) => {
        if (!eventList || eventList.length === 0) return;
        try {
            const eventIds = eventList.map(e => e.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const aMap = {};
            const pMap = {};
            const wMap = {};
            Object.entries(statusMap).forEach(([id, status]) => {
                aMap[id] = status.attended;
                pMap[id] = status.performed || false;
                wMap[id] = status.worked || false;
            });
            setAttendedMap(aMap);
            setPerformedMap(pMap);
            setWorkedMap(wMap);
        } catch (e) { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setTogglingMap(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    useEffect(() => {
        axios.get(`${config.API_URL}/promoters/${slug}`)
            .then(res => {
                setData(res.data);
                setLoading(false);
                if (res.data && res.data.promoter) {
                    document.title = `SHARC - ${res.data.promoter.name}`;
                }
                if (res.data && res.data.events) {
                    loadAttendedStatus(res.data.events);
                }
            })
            .catch(() => {
                setError('Failed to load promoter');
                setLoading(false);
            });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    const togglePhotos = async () => {
        const opening = !photosOpen;
        setPhotosOpen(opening);
        if (opening && !photosLoaded && data) {
            setPhotosLoading(true);
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/images`, {
                    params: { entityType: 'promoter', entityId: data.promoter.id },
                    headers: { Authorization: token },
                });
                setPhotos(res.data.images || []);
                setPhotosLoaded(true);
            } catch {
                setPhotosError('Failed to load photos.');
            } finally {
                setPhotosLoading(false);
            }
        }
    };

    const handleSetDefault = async (image) => {
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'promoter', entityId: data.promoter.id },
                { headers: { Authorization: token } }
            );
            window.location.reload();
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        }
    };

    if (loading) return <div style={styles.message}>Loading promoter...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { promoter, stats, links, events, festivals, festival_events } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const location = [promoter.city, promoter.state, promoter.country].filter(Boolean).join(', ');

    const q = search.toLowerCase();

    // Events tab filter
    const filteredEvents = search
        ? events.filter(e => [
            e.event_date ? formatDate(e.event_date) : '',
            e.venue_name, e.venue_city, e.venue_state, e.venue_country, e.festival_name, e.artists
          ].some(v => v && v.toLowerCase().includes(q)))
        : events;

    // Festivals tab filter
    const filteredFestivals = search
        ? festivals.filter(f => [
            f.festival_name,
            f.start_date ? formatDate(f.start_date) : '',
            f.artists
          ].some(v => v && v.toLowerCase().includes(q)))
        : festivals;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/promoters" style={styles.backLink}>← Back to Promoters</Link>
                <Link to={`/promoters/${promoter.slug || promoter.id}/edit`} style={styles.editButton}>Edit Promoter</Link>
            </div>

            {/* Header */}
            <div style={styles.header}>
                <div style={promoter.image_url ? styles.headerWithImage : undefined}>
                    {promoter.image_url && (
                        <img
                            src={promoter.image_url}
                            alt={promoter.name}
                            style={styles.promoterImage}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <div style={styles.headerText}>
                        <h1 style={styles.title}>{promoter.name}</h1>
                        {location && <div style={styles.location}>{location}</div>}
                        {links && links.length > 0 && (
                            <div style={styles.links}>
                                {links.map(l => (
                                    <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" style={styles.linkTag}>
                                        {l.description || l.url}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {promoter.description && (
                    <div style={styles.bio}>{promoter.description}</div>
                )}
            </div>

            {/* Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_events || 0}</div>
                    <div style={styles.statLabel}>Events</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_festivals || 0}</div>
                    <div style={styles.statLabel}>Festivals</div>
                </div>
            </div>

            {/* Photos — collapsible */}
            <div style={styles.collapsibleSection}>
                <button onClick={togglePhotos} style={styles.collapsibleHeader}>
                    <span style={styles.collapsibleTitle}>📷 Photos</span>
                    <span style={styles.collapsibleChevron}>{photosOpen ? '▲' : '▼'}</span>
                </button>
                {photosOpen && (
                    <div style={styles.collapsibleBody}>
                        {photosLoading && <div style={styles.photosEmpty}>Loading photos…</div>}
                        {photosError && <div style={styles.photosEmpty}>{photosError}</div>}
                        {!photosLoading && !photosError && photos.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📷</div>
                                <div style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>No photos yet for this promoter.</div>
                                <a href="/my-images" style={styles.uploadLink}>+ Upload a Photo</a>
                                <div style={{ color: '#555', fontSize: '12px', marginTop: '8px' }}>After uploading, assign it to this promoter using ID #{promoter.id}</div>
                            </div>
                        )}
                        {!photosLoading && photos.length > 0 && (
                            <div style={styles.photoGrid}>
                                {photos.map(img => (
                                    <div key={img.id} style={styles.photoCard}>
                                        <div style={styles.photoThumb} onClick={() => setLightbox(img)}>
                                            <img src={img.image_url} alt={img.caption || ''} style={styles.photoThumbImg} loading="lazy" />
                                            {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                        </div>
                                        <div style={styles.photoCardBody}>
                                            {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                            {img.is_public && (
                                                <button onClick={() => handleSetDefault(img)} style={styles.setDefaultBtn}>
                                                    ★ Set as Default
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
                    <div style={{ position: 'relative' }}>
                        <img src={lightbox.image_url} alt={lightbox.caption || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
                        {lightbox.caption && <div style={styles.lightboxCaption}>{lightbox.caption}</div>}
                        <button onClick={() => setLightbox(null)} style={styles.lightboxClose}>✕</button>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => { setActiveTab(i); setSearch(''); setExpandedFestival(null); }}
                            style={{ ...styles.tabBtn, ...(activeTab === i ? styles.tabBtnActive : {}) }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div style={styles.tabContent}>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={styles.tabSearch}
                    />

                    {/* Events Tab */}
                    {activeTab === 0 && (
                        filteredEvents.length === 0 ? (
                            <div style={styles.emptyTab}>{search ? 'No events match your search.' : 'No events linked to this promoter yet.'}</div>
                        ) : (
                            <div style={styles.tableContainer}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>#</th>
                                            <th style={styles.th}>Date</th>
                                            <th style={styles.th}>Venue</th>
                                            <th style={styles.th}>City</th>
                                            <th style={styles.th}>State</th>
                                            <th style={styles.th}>Country</th>
                                            <th style={styles.th}>Artists</th>
                                            <th style={styles.th}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEvents.map((e, idx) => {
                                            const isAttended = attendedMap[e.event_id] === true;
                                            const isPerformed = performedMap[e.event_id] === true;
                                            const isWorked = workedMap[e.event_id] === true;
                                            const isToggling = togglingMap[e.event_id];
                                            return (
                                            <tr key={e.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                                <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                    <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                        {filteredEvents.length - idx}
                                                    </Link>
                                                </td>
                                                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                                    {formatDate(e.event_date)}
                                                </td>
                                                <td style={styles.td}>
                                                    <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                        {e.venue_name}
                                                    </Link>
                                                </td>
                                                <td style={styles.td}>{e.venue_city || '—'}</td>
                                                <td style={styles.td}>{e.venue_state || '—'}</td>
                                                <td style={styles.td}>{e.venue_country || '—'}</td>
                                                <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                    {e.festival_name && e.festival_slug
                                                        ? <><Link to={`/festivals/${e.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{e.festival_name}</Link>{e.artists ? ` - ${e.artists}` : ''}</>
                                                        : e.artists || '—'
                                                    }
                                                </td>
                                                <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {isPerformed ? (
                                                        <span style={styles.performedBadge}>Performed</span>
                                                    ) : isWorked ? (
                                                        <span style={styles.workedBadge}>Worked</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleToggleAttended(e.event_id)}
                                                            disabled={isToggling}
                                                            style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}
                                                        >
                                                            {isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    {/* Festivals Tab */}
                    {activeTab === 1 && (
                        filteredFestivals.length === 0 ? (
                            <div style={styles.emptyTab}>{search ? 'No festivals match your search.' : 'No festivals linked to this promoter yet.'}</div>
                        ) : (
                            <div style={styles.tableContainer}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}></th>
                                            <th style={styles.th}>Festival</th>
                                            <th style={styles.th}>Start Date</th>
                                            <th style={styles.th}>End Date</th>
                                            <th style={styles.th}>Headliners</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFestivals.map((f, idx) => {
                                            const isExpanded = expandedFestival === f.festival_id;
                                            const festEvents = (festival_events || []).filter(e => e.festival_id === f.festival_id);
                                            return (
                                                <React.Fragment key={f.festival_id}>
                                                    <tr
                                                        style={{ ...(idx % 2 === 0 ? styles.trEven : styles.trOdd), cursor: festEvents.length > 0 ? 'pointer' : 'default' }}
                                                        onClick={() => festEvents.length > 0 && setExpandedFestival(isExpanded ? null : f.festival_id)}
                                                    >
                                                        <td style={{ ...styles.td, width: '28px', color: '#bb86fc', fontSize: '12px' }}>
                                                            {festEvents.length > 0 ? (isExpanded ? '▾' : '▸') : ''}
                                                        </td>
                                                        <td style={styles.td}>
                                                            <Link to={`/festivals/${f.festival_slug || f.festival_id}`} style={styles.tableLink} onClick={e => e.stopPropagation()}>
                                                                {f.festival_name}
                                                            </Link>
                                                        </td>
                                                        <td style={styles.td}>{formatDate(f.start_date)}</td>
                                                        <td style={styles.td}>{formatDate(f.end_date)}</td>
                                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{f.headliners || '—'}</td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={5} style={{ padding: '0', backgroundColor: '#131313', borderBottom: '1px solid #333' }}>
                                                                <table style={{ ...styles.table, margin: 0 }}>
                                                                    <thead>
                                                                        <tr style={{ backgroundColor: '#1a1a1a' }}>
                                                                            <th style={{ ...styles.th, paddingLeft: '40px' }}>Date</th>
                                                                            <th style={styles.th}>City</th>
                                                                            <th style={styles.th}>State</th>
                                                                            <th style={styles.th}>Country</th>
                                                                            <th style={styles.th}>Venue</th>
                                                                            <th style={styles.th}>Artists</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {festEvents.map((e, ei) => (
                                                                            <tr key={e.event_id} style={{ backgroundColor: ei % 2 === 0 ? '#161616' : '#131313' }}>
                                                                                <td style={{ ...styles.td, paddingLeft: '40px' }}>
                                                                                    <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                                                        {formatDate(e.event_date)}
                                                                                    </Link>
                                                                                </td>
                                                                                <td style={styles.td}>{e.venue_city || '—'}</td>
                                                                                <td style={styles.td}>{e.venue_state || '—'}</td>
                                                                                <td style={styles.td}>{e.venue_country || '—'}</td>
                                                                                <td style={styles.td}>
                                                                                    <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                                                        {e.venue_name}
                                                                                    </Link>
                                                                                </td>
                                                                                <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{e.artists || '—'}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold' },
    header: { marginBottom: '24px' },
    headerWithImage: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '16px' },
    headerText: { flex: 1 },
    promoterImage: { width: '140px', height: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    title: { fontSize: '40px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '6px' },
    location: { fontSize: '16px', color: '#aaa', marginBottom: '10px' },
    links: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' },
    linkTag: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', color: '#bb86fc', fontSize: '13px', textDecoration: 'none' },
    bio: { fontSize: '15px', color: '#ccc', lineHeight: '1.7', marginTop: '16px', padding: '16px', backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #2a2a2a' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', borderBottom: '2px solid #2a2a2a', marginBottom: '0' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', color: '#aaa', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabBtnActive: { color: '#bb86fc', borderBottomColor: '#bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '100px' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
    // Collapsible photos
    collapsibleSection: { marginBottom: '32px' },
    collapsibleHeader: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '14px 20px', cursor: 'pointer', color: '#e0e0e0' },
    collapsibleTitle: { fontSize: '16px', fontWeight: '600' },
    collapsibleChevron: { fontSize: '12px', color: '#888' },
    collapsibleBody: { backgroundColor: '#1a1a1a', border: '1px solid #333', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '20px' },
    photosEmpty: { textAlign: 'center', color: '#666', padding: '20px 0', fontSize: '14px' },
    uploadLink: { backgroundColor: '#bb86fc', color: '#121212', borderRadius: '6px', padding: '7px 16px', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },
};

export default PromoterDetail;
